import React, { useState, useRef, useCallback } from 'react';
import { Box, TextField, IconButton, InputAdornment, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import CloseIcon from '@mui/icons-material/Close';
import { useMessaging } from '../../contexts/MessagingContext';
import { useTypingIndicator } from '../../hooks/useTypingIndicator';
import MentionInput from '../common/MentionInput';
import { SelectedMention } from '../../types/mention';
import { mentionParser } from '../../utils/mention-parser';

interface MessageInputProps {
  conversationId: string;
  replyTo?: string | null;
  onClearReply?: () => void;
}

/**
 * MessageInput Component
 *
 * Text input for composing and sending messages with:
 * - Typing indicators
 * - Reply preview
 * - Send on Enter (Shift+Enter for new line)
 * - Emoji and attachment placeholders
 */
export const MessageInput: React.FC<MessageInputProps> = ({
  conversationId,
  replyTo,
  onClearReply,
}) => {
  const { sendMessage } = useMessaging();
  const { startTyping, stopTyping } = useTypingIndicator(conversationId);
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [selectedMentions, setSelectedMentions] = useState<SelectedMention[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleMentionChange = (newValue: string, newMentions: SelectedMention[]): void => {
    setContent(newValue);
    setSelectedMentions(newMentions);
    startTyping();
  };

  const handleSend = useCallback(async (): Promise<void> => {
    if (!content.trim() || sending) return;

    setSending(true);
    try {
      // Parse mentions from content and selected mentions
      const parsedMentions = mentionParser.parse(content, selectedMentions);

      await sendMessage({
        conversationId,
        type: 'text',
        content: content.trim(),
        replyTo: replyTo || undefined,
        mentions: parsedMentions,
      });

      setContent('');
      setSelectedMentions([]);
      stopTyping();
      if (onClearReply) onClearReply();
      inputRef.current?.focus();
    } catch (error: any) {
      console.error('Error sending message:', error);
      alert(`Failed to send message: ${error.message}`);
    } finally {
      setSending(false);
    }
  }, [content, selectedMentions, sending, conversationId, replyTo, sendMessage, stopTyping, onClearReply]);

  const handleKeyPress = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
      {replyTo && (
        <Box
          sx={{
            mb: 1,
            p: 1,
            bgcolor: 'action.hover',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Replying to message...
          </Typography>
          <IconButton size="small" onClick={onClearReply}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
      <MentionInput
        value={content}
        onChange={handleMentionChange}
        contentType="message"
        conversationId={conversationId}
        placeholder="Type a message... (@ to mention)"
        maxRows={4}
        fullWidth
        disabled={sending}
        onKeyPress={handleKeyPress}
        onBlur={stopTyping}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <IconButton size="small" disabled>
                <AttachFileIcon />
              </IconButton>
              <IconButton size="small" disabled>
                <EmojiEmotionsIcon />
              </IconButton>
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                color="primary"
                onClick={handleSend}
                disabled={!content.trim() || sending}
              >
                <SendIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
};
